import { Breadcrumb, Flex } from 'antd';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Routes } from '@shared/constants/routes';
import { toChannelTypeFromRoute, type ChannelCreatableType } from '../../lib/channel-type';
import { useCreateChannel } from '../../hooks/use-create-channel';
import { TelegramCreateForm } from './forms/telegram-create-form';
import { VkCreateForm } from './forms/vk-create-form';
import { WidgetCreateForm } from './forms/widget-create-form';
import { MaxCreateForm } from './forms/max-create-form';
import { AvitoCreateForm } from './forms/avito-create-form';
import type {
  ChannelAvitoFormValues,
  ChannelMaxFormValues,
  ChannelTelegramFormValues,
  ChannelVkFormValues,
  ChannelWidgetFormValues,
} from '../../lib/types';
import styles from './channel-create.module.scss';
import { ScrollContainer } from '@shared/ui/scroll-container';

const CHANNEL_PAGE_TITLES: Record<ChannelCreatableType, string> = {
  telegram: 'breadcrumbs.telegram',
  telegram_bot: 'breadcrumbs.telegram_bot',
  vk_bot: 'breadcrumbs.vk_bot',
  widget: 'breadcrumbs.widget',
  max_bot: 'breadcrumbs.max_bot',
  avito: 'breadcrumbs.avito',
};

export const ChannelCreatePage: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'channels' });
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const channelType = useMemo(() => toChannelTypeFromRoute(type), [type]);
  const preselectedAgentId = useMemo(() => {
    const rawAgentId = searchParams.get('ai-agent') ?? searchParams.get('ai-agent-id');
    const normalizedAgentId = Number(rawAgentId);

    if (!Number.isInteger(normalizedAgentId) || normalizedAgentId <= 0) {
      return undefined;
    }

    return normalizedAgentId;
  }, [searchParams]);
  const createDraftKey = useMemo(() => {
    const agentKey = preselectedAgentId ?? 'no-agent';
    return `channels:create:${channelType ?? 'unknown'}:${agentKey}`;
  }, [channelType, preselectedAgentId]);

  useEffect(() => {
    if (!channelType) {
      navigate(Routes.channels.root, { replace: true });
    }
  }, [channelType, navigate]);

  const {
    createChannelVkBot,
    isCreateVkBotChannelLoading,
    createChannelWidget,
    isCreateWidgetChannelLoading,
    createTelegramChannel,
    isCreateAgentLoading,
    createMaxBotChannel,
    isCreateMaxBotChannelLoading,
    createAvitoChannel,
    isCreateAvitoChannelLoading,
  } = useCreateChannel();

  const navigateAfterFormAction = () => {
    if (preselectedAgentId) {
      navigate(generatePath(Routes.aiAgents.agent, { agentId: preselectedAgentId }));
      return;
    }

    navigate(Routes.channels.root);
  };

  const handleSubmitTelegram = async (values: ChannelTelegramFormValues) => {
    await createTelegramChannel(values);
    navigateAfterFormAction();
  };

  const handleSubmitVk = async (values: ChannelVkFormValues) => {
    await createChannelVkBot(values);
    navigateAfterFormAction();
  };

  const handleSubmitWidget = async (values: ChannelWidgetFormValues) => {
    await createChannelWidget(values);
    navigateAfterFormAction();
  };

  const handleSubmitMax = async (values: ChannelMaxFormValues) => {
    await createMaxBotChannel(values);
    navigateAfterFormAction();
  };

  const handleSubmitAvito = async (values: ChannelAvitoFormValues) => {
    await createAvitoChannel(values);
    navigateAfterFormAction();
  };

  if (!channelType) return null;

  return (
    <Flex vertical gap={20} className={styles.root}>
      <Breadcrumb
        items={[
          {
            title: t('channels'),
            onClick: () => navigate(Routes.channels.root),
          },
          {
            title: CHANNEL_PAGE_TITLES[channelType]
              ? t(CHANNEL_PAGE_TITLES[channelType])
              : t('createChannel'),
          },
        ]}
      />

      <ScrollContainer>
        <Flex vertical gap={16} className={styles.container}>
          {(channelType === 'telegram' || channelType === 'telegram_bot') && (
            <TelegramCreateForm
              isSubmitLoading={isCreateAgentLoading}
              onSubmit={handleSubmitTelegram}
              onClose={navigateAfterFormAction}
              preselectedAgentId={preselectedAgentId}
              draftKey={createDraftKey}
            />
          )}

          {channelType === 'vk_bot' && (
            <VkCreateForm
              isSubmitLoading={isCreateVkBotChannelLoading}
              onSubmit={handleSubmitVk}
              onClose={navigateAfterFormAction}
              preselectedAgentId={preselectedAgentId}
              draftKey={createDraftKey}
            />
          )}

          {channelType === 'widget' && (
            <WidgetCreateForm
              isSubmitLoading={isCreateWidgetChannelLoading}
              onSubmit={handleSubmitWidget}
              onClose={navigateAfterFormAction}
              preselectedAgentId={preselectedAgentId}
              draftKey={createDraftKey}
            />
          )}

          {channelType === 'max_bot' && (
            <MaxCreateForm
              isSubmitLoading={isCreateMaxBotChannelLoading}
              onSubmit={handleSubmitMax}
              onClose={navigateAfterFormAction}
              preselectedAgentId={preselectedAgentId}
              draftKey={createDraftKey}
            />
          )}

          {channelType === 'avito' && (
            <AvitoCreateForm
              isSubmitLoading={isCreateAvitoChannelLoading}
              onSubmit={handleSubmitAvito}
              onClose={navigateAfterFormAction}
              preselectedAgentId={preselectedAgentId}
              draftKey={createDraftKey}
            />
          )}
        </Flex>
      </ScrollContainer>
    </Flex>
  );
};
